/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * AvmEstimate.js  —  TEMPORARY internal tool (admin dash).
 *
 * Pick any property, get a comps-based estimate of what it would sell for.
 * Comps are condition-adjusted ($/sqft nudged by the condition gap × a flat
 * 4%/point coefficient). If the target is missing condition or LauftQ, score it
 * on demand right here and the estimate recomputes — so you can watch the visual
 * move the number. Live-reading: improves as data accumulates. Delete when done.
 */
import React, { useState, useCallback, useRef } from 'react';
import { Search, AlertCircle, Loader, Eye, Home, RefreshCw } from 'react-feather';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const C = {
  bg: '#fcfcfc', panel: '#eeebe3', accent: '#fdd700', ink: '#1a1a1a',
  sage: '#7a9471', border: '#e2ddd2', muted: '#8a8578',
};
const COND_ANCHORS = ['Dated', 'Builder-grade', 'Updated', 'Renovated', 'Luxury'];

const authHeaders = () => ({ Authorization: `Bearer ${AuthService.getToken()}` });
const fmt = (n) => n == null ? '—' : `$${Math.round(n).toLocaleString('en-US')}`;
const fmtPsf = (n) => n == null ? '—' : `$${Math.round(n)}/sf`;
const fmtDate = (ms) => { if (!ms) return '—'; const d = new Date(ms); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US'); };

const AvmEstimate = () => {
  const [searchMode, setSearchMode] = useState('address'); // 'address' | 'zip'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const [zip, setZip] = useState('');
  const [forSale, setForSale] = useState([]);
  const [loadingZip, setLoadingZip] = useState(false);

  const [selected, setSelected] = useState(null); // { id, address }
  const [data, setData] = useState(null);          // estimate response
  const [loading, setLoading] = useState(false);
  const [scoring, setScoring] = useState(null);    // 'lauftq' | 'condition' | null

  const debounceRef = useRef(null);

  const loadForSale = useCallback(async () => {
    if (!/^\d{5}$/.test(zip.trim())) { setError('Enter a 5-digit zip'); return; }
    setLoadingZip(true); setError(''); setForSale([]);
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-estimate/for-sale?zip=${zip.trim()}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load');
      setForSale(json.data.rows);
      if (json.data.rows.length === 0) setError(`No for-sale listings in ${zip.trim()}`);
    } catch (e) { setError(e.message); } finally { setLoadingZip(false); }
  }, [zip]);

  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 3) { setResults([]); return; }
    setSearching(true); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/vision-test/search?q=${encodeURIComponent(q.trim())}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Search failed');
      setResults(json.data);
    } catch (e) { setError(e.message); } finally { setSearching(false); }
  }, []);

  const onQueryChange = (e) => {
    const v = e.target.value; setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 350);
  };

  const estimate = useCallback(async (propertyId) => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-estimate?propertyId=${propertyId}`, { headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Estimate failed');
      setData(json.data);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);

  const select = (p) => {
    setSelected({ id: p.id, address: p.address });
    setData(null); setResults([]); setQuery(''); setForSale([]);
    estimate(p.id);
  };

  const score = async (action) => {
    if (!selected) return;
    setScoring(action);
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/avm-fill/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ propertyId: selected.id }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Scoring failed');
      if (!json.data.filled) setError(`${action}: ${json.data.reason || 'no result'}`);
      await estimate(selected.id); // recompute with the new scores
    } catch (e) { setError(e.message); } finally { setScoring(null); }
  };

  const t = data && data.target;
  const e = data && data.estimate;

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <h2 style={{ margin: 0, color: C.ink }}>AVM Estimate</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            Pick a property for a comps-based, condition-adjusted estimate. Score missing visuals on the spot.
          </div>
        </div>
        <div style={S.modeToggle}>
          <button style={searchMode === 'address' ? S.modeOn : S.modeOff} onClick={() => setSearchMode('address')}>Address</button>
          <button style={searchMode === 'zip' ? S.modeOn : S.modeOff} onClick={() => setSearchMode('zip')}>For-sale by zip</button>
        </div>
      </div>

      {/* Search */}
      {searchMode === 'address' ? (
        <div style={S.searchBar}>
          <Search size={18} color={C.muted} />
          <input style={S.searchInput} placeholder="Search by address (min 3 chars)…" value={query} onChange={onQueryChange} />
          {searching && <Loader size={16} color={C.muted} className="spin" />}
        </div>
      ) : (
        <div style={S.searchBar}>
          <Search size={18} color={C.muted} />
          <input
            style={S.searchInput}
            placeholder="Enter a 5-digit zip to list for-sale listings…"
            value={zip}
            onChange={(ev) => setZip(ev.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyDown={(ev) => ev.key === 'Enter' && loadForSale()}
          />
          <button style={S.loadBtn} onClick={loadForSale} disabled={loadingZip}>
            {loadingZip ? <Loader size={15} className="spin" /> : 'List for sale'}
          </button>
        </div>
      )}

      {error && <div style={S.errorBar}><AlertCircle size={16} /> {error}</div>}

      {/* Address results */}
      {searchMode === 'address' && results.length > 0 && (
        <div style={S.resultList}>
          {results.map((p) => (
            <div key={p.id} style={S.resultRow} onClick={() => select(p)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.resultAddr}>{p.address}</div>
                <div style={S.resultMeta}>{p.price}{p.status ? ` · ${p.status}` : ''}</div>
              </div>
              <button style={S.selectBtn}>Estimate</button>
            </div>
          ))}
        </div>
      )}

      {/* For-sale-by-zip results */}
      {searchMode === 'zip' && forSale.length > 0 && (
        <div style={S.resultList}>
          {forSale.map((p) => (
            <div key={p.id} style={S.resultRow} onClick={() => select(p)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.resultAddr}>{p.address}</div>
                <div style={S.resultMeta}>
                  {p.listPrice ? fmt(p.listPrice) : 'no list price'}
                  {p.sqft ? ` · ${p.sqft.toLocaleString()} sqft` : ''}
                  {' · '}
                  <span style={{ color: p.hasCondition ? C.sage : C.muted }}>cond {p.hasCondition ? '✓' : '✗'}</span>
                  {' · '}
                  <span style={{ color: p.hasLauftq ? C.sage : C.muted }}>lauftq {p.hasLauftq ? '✓' : '✗'}</span>
                </div>
              </div>
              <button style={S.selectBtn}>Estimate</button>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div style={S.center}><Loader size={26} color={C.sage} className="spin" /><p style={{ color: C.muted, marginTop: 10 }}>Pulling comps…</p></div>
      )}

      {/* Target + estimate */}
      {t && !loading && (
        <div style={S.grid}>
          {/* Target card */}
          <div style={S.card}>
            <div style={S.cardTitle}>{t.address}</div>
            <div style={S.sub}>{t.csz}</div>
            <div style={S.specRow}>
              <span>{t.sqft ? `${t.sqft.toLocaleString()} sqft` : 'no sqft'}</span>
              <span>{t.beds ?? '—'} bd</span>
              <span>{t.baths ?? '—'} ba</span>
              {t.soldPrice && <span style={{ color: C.sage }}>sold {fmt(t.soldPrice)}</span>}
            </div>

            {/* Condition */}
            <div style={S.scoreBlock}>
              <div style={S.scoreLabel}>Condition {t.avgCondition != null && <span style={{ color: C.muted }}>· avg {t.avgCondition.toFixed(1)}</span>}</div>
              {t.hasCondition ? (
                <div style={S.condRow}>
                  {[['Kit', t.condition.kitchen], ['Bath', t.condition.bath], ['Ext', t.condition.exterior], ['Liv', t.condition.living]].map(([lbl, v]) => (
                    <span key={lbl} style={S.condItem}>{lbl} {v == null ? '—' : <b title={COND_ANCHORS[v - 1]}>{v}</b>}</span>
                  ))}
                </div>
              ) : (
                <button style={t.hasLauftq ? S.scoreBtn : S.scoreBtnDisabled} onClick={() => score('condition')} disabled={!t.hasLauftq || scoring === 'condition'} title={t.hasLauftq ? '' : 'Run LauftQ first'}>
                  {scoring === 'condition' ? <Loader size={13} className="spin" /> : <Home size={13} />} Get condition
                </button>
              )}
            </div>

            {/* LauftQ */}
            <div style={S.scoreBlock}>
              <div style={S.scoreLabel}>LauftQ</div>
              {t.hasLauftq ? (
                <span style={S.okPill}>scored</span>
              ) : (
                <button style={S.scoreBtn} onClick={() => score('lauftq')} disabled={scoring === 'lauftq'}>
                  {scoring === 'lauftq' ? <Loader size={13} className="spin" /> : <Eye size={13} />} Get LauftQ
                </button>
              )}
            </div>
          </div>

          {/* Estimate card */}
          <div style={S.card}>
            {e && (e.mode === 'condition_adjusted' || e.mode === 'location_size') ? (
              <>
                <div style={S.estLabel}>Estimated value</div>
                <div style={S.estPoint}>{fmt(e.point)}</div>
                <div style={S.estRange}>{fmt(e.low)} – {fmt(e.high)}</div>
                <div style={S.estPsf}>{fmtPsf(e.medianPsf)} median · target {t.sqft.toLocaleString()} sqft</div>
                <div style={S.modeRow}>
                  <span style={e.mode === 'condition_adjusted' ? S.modeGood : S.modeWarn}>
                    {e.mode === 'condition_adjusted' ? 'Condition-adjusted' : 'Location + size only'}
                  </span>
                  <span style={S.modeMeta}>{e.compCount} comps · {e.adjustedCount} cond-adjusted · {e.tier}</span>
                </div>
                {e.mode === 'location_size' && (
                  <div style={S.hint}>No condition score on the target — this is a plain comps estimate. Score condition above to refine it.</div>
                )}
                {e.mode === 'condition_adjusted' && e.adjustedCount < e.compCount && (
                  <div style={S.hint}>{e.compCount - e.adjustedCount} of {e.compCount} comps had no condition score and were used raw.</div>
                )}
              </>
            ) : e && e.mode === 'insufficient' ? (
              <div style={S.center}>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>Not enough comparable sales</div>
                <p style={{ color: C.muted, marginTop: 8, fontSize: 13 }}>
                  Found {e.compCount} sold comp{e.compCount === 1 ? '' : 's'} (need {e.minComps}). Fill more sold prices in this area, then retry.
                </p>
              </div>
            ) : e && e.mode === 'no_sqft' ? (
              <div style={S.center}><p style={{ color: C.muted }}>No sqft on this property — can't compute $/sqft.</p></div>
            ) : e && e.mode === 'no_location' ? (
              <div style={S.center}><p style={{ color: C.muted }}>No parseable city/zip — can't find comps.</p></div>
            ) : null}
            <button style={S.reBtn} onClick={() => estimate(t.id)} disabled={loading}>
              <RefreshCw size={13} /> Recompute
            </button>
          </div>
        </div>
      )}

      {/* Comps table */}
      {e && Array.isArray(e.comps) && e.comps.length > 0 && (
        <div style={S.tableWrap}>
          <div style={S.tableCap}>Comps used{e.tier ? ` · ${e.tier}` : ''}</div>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Address</th>
              <th style={S.thR}>Sold</th>
              <th style={S.thR}>Sqft</th>
              <th style={S.thR}>$/sf raw</th>
              <th style={S.thC}>Cond</th>
              <th style={S.thR}>$/sf adj</th>
              <th style={S.thR}>Sold date</th>
            </tr></thead>
            <tbody>
              {e.comps.map((c) => (
                <tr key={c.id} style={S.tr}>
                  <td style={S.td}>{c.address}<div style={S.sub}>{c.csz}</div></td>
                  <td style={S.tdR}>{fmt(c.soldPrice)}</td>
                  <td style={S.tdR}>{c.sqft ? c.sqft.toLocaleString() : '—'}</td>
                  <td style={S.tdR}>{fmtPsf(c.rawPsf)}</td>
                  <td style={S.tdC}>{c.avgCondition == null ? <span style={{ color: C.muted }}>—</span> : c.avgCondition.toFixed(1)}</td>
                  <td style={{ ...S.tdR, color: c.adjusted ? C.sage : C.ink, fontWeight: c.adjusted ? 700 : 400 }}>{fmtPsf(c.adjPsf)}</td>
                  <td style={S.tdR}>{fmtDate(c.soldDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const S = {
  wrap: { padding: 24, background: C.bg, minHeight: '100%', fontFamily: 'system-ui, sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  modeToggle: { display: 'flex', background: C.panel, borderRadius: 9, padding: 3, flexShrink: 0 },
  modeOn: { background: '#fff', color: C.ink, border: 'none', borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
  modeOff: { background: 'transparent', color: C.muted, border: 'none', borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  loadBtn: { background: C.accent, color: C.ink, border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer', minWidth: 110, flexShrink: 0 },
  center: { textAlign: 'center', padding: 30 },
  searchBar: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 12 },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 15, background: 'transparent', color: C.ink },
  errorBar: { display: 'flex', alignItems: 'center', gap: 8, background: '#fdecea', color: '#b71c1c', border: '1px solid #f5c6cb', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 14 },
  resultList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },
  resultRow: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, cursor: 'pointer' },
  resultAddr: { color: C.ink, fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  resultMeta: { color: C.muted, fontSize: 12, marginTop: 2 },
  selectBtn: { background: C.accent, color: C.ink, border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 },
  card: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, position: 'relative' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: C.ink },
  sub: { fontSize: 12, color: C.muted, marginTop: 2 },
  specRow: { display: 'flex', gap: 14, marginTop: 10, fontSize: 13, color: C.ink, flexWrap: 'wrap' },
  scoreBlock: { marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` },
  scoreLabel: { fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 },
  condRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  condItem: { fontSize: 13, color: C.ink },
  scoreBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.accent, color: C.ink, border: 'none', borderRadius: 8, padding: '7px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  scoreBtnDisabled: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.panel, color: C.muted, border: 'none', borderRadius: 8, padding: '7px 12px', fontWeight: 600, fontSize: 13, cursor: 'not-allowed' },
  okPill: { display: 'inline-block', background: '#f1f5ef', color: '#3f5c37', border: `1px solid ${C.sage}66`, borderRadius: 6, padding: '4px 10px', fontWeight: 600, fontSize: 12 },
  estLabel: { fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 },
  estPoint: { fontSize: 34, fontWeight: 800, color: C.ink, lineHeight: 1.1, marginTop: 6 },
  estRange: { fontSize: 15, color: C.sage, fontWeight: 600, marginTop: 4 },
  estPsf: { fontSize: 13, color: C.muted, marginTop: 6 },
  modeRow: { marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 },
  modeGood: { alignSelf: 'flex-start', background: C.sage, color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  modeWarn: { alignSelf: 'flex-start', background: '#f3ead2', color: '#8a6d1f', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  modeMeta: { fontSize: 12, color: C.muted },
  hint: { fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.4 },
  reBtn: { position: 'absolute', top: 14, right: 14, display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: C.ink, cursor: 'pointer' },
  tableWrap: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'auto' },
  tableCap: { padding: '10px 14px', fontSize: 12, fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.border}` },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '9px 12px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  thR: { textAlign: 'right', padding: '9px 12px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  thC: { textAlign: 'center', padding: '9px 8px', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${C.border}` },
  td: { padding: '9px 12px', color: C.ink },
  tdR: { padding: '9px 12px', color: C.ink, textAlign: 'right', whiteSpace: 'nowrap' },
  tdC: { padding: '9px 8px', color: C.ink, textAlign: 'center' },
};

export default AvmEstimate;